import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Type } from '@angular/core';
import { EventSeverityDisplayModule, SubscriptionButtonModule } from '@iot-platform/iot-platform-ui';
import { CronDisplayPipe, DateFormatPipe, InfoDisplayPipe, NumberFormatPipe, UserNamePipe, ValueUnitFormatPipe } from '@iot-platform/pipes';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { noop } from 'rxjs';
import { ActiveEventsCellComponent } from './components/cells/active-events-cell/active-events-cell.component';
import { AddressAccuracyCellComponent } from './components/cells/address-accuracy-cell/address-accuracy-cell.component';
import { ArrayOfObjectsCellComponent } from './components/cells/array-of-objects-cell/array-of-objects-cell.component';
import { AssetTemplateCellComponent } from './components/cells/asset-template-cell/asset-template-cell.component';
import { AssetVariableCellComponent } from './components/cells/asset-variable-cell/asset-variable-cell.component';
import { AssetVariableThresholdCellComponent } from './components/cells/asset-variable-threshold-cell/asset-variable-threshold-cell.component';
import { AuditTrailAttributesCellComponent } from './components/cells/audit-trail-attributes-cell/audit-trail-attributes-cell.component';
import { BasicCellComponent } from './components/cells/basic-cell/basic-cell.component';
import { BasicLinkCellComponent } from './components/cells/basic-link-cell/basic-link-cell.component';
import { BooleanCellComponent } from './components/cells/boolean-cell/boolean-cell.component';
import { CallToActionCellComponent } from './components/cells/call-to-action-cell/call-to-action-cell.component';
import { CallToActionHeaderComponent } from './components/cells/call-to-action-header/call-to-action-header.component';
import { ColorCellComponent } from './components/cells/color-cell/color-cell.component';
import { CommentCellComponent } from './components/cells/comment-cell/comment-cell.component';
import { CountCellComponent } from './components/cells/count-cell/count-cell.component';
import { CronReadableCellComponent } from './components/cells/cron-readable-cell/cron-readable-cell.component';
import { CyclicalCallFaultCellComponent } from './components/cells/cyclical-call-fault-cell/cyclical-call-fault-cell.component';
import { DateCellComponent } from './components/cells/date-cell/date-cell.component';
import { DiagnosticVariableCellComponent } from './components/cells/diagnostic-variable-cell/diagnostic-variable-cell.component';
import { DynamicDataCellComponent } from './components/cells/dynamic-data-cell/dynamic-data-cell.component';
import { DynamicIconCellComponent } from './components/cells/dynamic-icon-cell/dynamic-icon-cell.component';
import { EmailTemplateCellComponent } from './components/cells/email-template-cell/email-template-cell.component';
import { EntityCellComponent } from './components/cells/entity-cell/entity-cell.component';
import { EventDurationCellComponent } from './components/cells/event-duration-cell/event-duration-cell.component';
import { FollowedNumberCellComponent } from './components/cells/followed-number-cell/followed-number-cell.component';
import { IconCellComponent } from './components/cells/icon-cell/icon-cell.component';
import { LastCommandStatusCellComponent } from './components/cells/last-command-status-cell/last-command-status-cell.component';
import { LinkedCellComponent } from './components/cells/linked-cell/linked-cell.component';
import { NumberCellComponent } from './components/cells/number-cell/number-cell.component';
import { OutgoingConnectorCellComponent } from './components/cells/outgoing-connector-cell/outgoing-connector-cell.component';
import { RichVariableValueCellComponent } from './components/cells/rich-variable-value-cell/rich-variable-value-cell.component';
import { SeverityCellComponent } from './components/cells/severity-cell/severity-cell.component';
import { TagCellComponent } from './components/cells/tag-cell/tag-cell.component';
import { TopicSelfSubscriptionCellComponent } from './components/cells/topic-self-subscription-cell/topic-self-subscription-cell.component';
import { TranslatedCellComponent } from './components/cells/translated-cell/translated-cell.component';
import { UserCellComponent } from './components/cells/user-cell/user-cell.component';
import { VariableDispatchCellComponent } from './components/cells/variable-dispatch-cell/variable-dispatch-cell.component';
import { AuditTrailAttributesTextFilterComponent } from './components/filters/audit-trail-attributes-cell-text-filter/audit-trail-attributes-text-filter.component';
import { RichVariableValueCellFilterComponent } from './components/filters/rich-variable-value-cell-filter/rich-variable-value-cell-filter.component';
import { UserCellTextFilterComponent } from './components/filters/user-cell-text-filter/user-cell-text-filter.component';
import { GridExportDialogComponent } from './components/grid-export/grid-export-dialog/grid-export-dialog.component';
import { GridExportComponent } from './components/grid-export/grid-export.component';
import { GridManagerAdminPopupComponent } from './components/grid-manager-popups/grid-manager-admin-popup/grid-manager-admin-popup.component';
import { GridManagerUserPopupComponent } from './components/grid-manager-popups/grid-manager-user-popup/grid-manager-user-popup.component';
import { BasicHeaderCellComponent } from './components/header-cells/basic-header-cell/basic-header-cell.component';
import { EmptyHeaderCellComponent } from './components/header-cells/empty-header-cell/empty-header-cell.component';
import { GroupHeaderCellComponent } from './components/header-cells/group-header-cell/group-header-cell.component';
import { HeaderShellComponent } from './components/header-cells/header-shell/header-shell.component';
import { IconAndTextHeaderCellComponent } from './components/header-cells/icon-and-text-header-cell/icon-and-text-header-cell.component';
import { IconHeaderCellComponent } from './components/header-cells/icon-header-cell/icon-header-cell.component';
import { PaginatorComponent } from './components/pagination/paginator/paginator.component';
import { RowGroupInnerRendererCellComponent } from './components/row-group-cells/row-group-inner-renderer-cell/row-group-inner-renderer-cell.component';
import { GridManagerComponent } from './containers/grid-manager/grid-manager.component';
import { GridPageComponent } from './containers/grid-page/grid-page.component';
import { GridEngineComponent } from './containers/legacy-grid-engine/grid-engine.component';
import { AbstractGridAssetVariableConfigureReadPopupService } from './services/abstract-grid-asset-variable-configure-read-popup.service';
import { GridEngineSettingsService } from './services/grid-engine-settings.service';
import { GridsEffects } from './state/effects/grids.effects';
import * as fromGrids from './state/reducers';
import { enableDevValidations } from 'ag-grid-community';

// Type-only: Node globals are not in tsconfig types; the bundler replaces process.env.NODE_ENV at build time.
declare const process: { env: { NODE_ENV?: string } };

if (process.env.NODE_ENV !== 'production') {
  enableDevValidations({
    showOverlayOn: ['deprecation', 'error'],
    throwOn: ['warning', 'error'],
    // #9: CSS variables read before stylesheets load (benign, always in jsdom) — AG Grid falls back to defaults
    suppress: [9]
  });
}

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

provideGlobalGridOptions({
  theme: 'legacy'
});

const FILTER_COMPONENTS = [RichVariableValueCellFilterComponent, UserCellTextFilterComponent, AuditTrailAttributesTextFilterComponent];
const CELLS_COMPONENTS = [
  ActiveEventsCellComponent,
  AddressAccuracyCellComponent,
  ArrayOfObjectsCellComponent,
  AssetTemplateCellComponent,
  AssetVariableCellComponent,
  AssetVariableThresholdCellComponent,
  BasicCellComponent,
  BasicLinkCellComponent,
  BooleanCellComponent,
  CallToActionCellComponent,
  CallToActionHeaderComponent,
  ColorCellComponent,
  CommentCellComponent,
  CountCellComponent,
  CronReadableCellComponent,
  CyclicalCallFaultCellComponent,
  DateCellComponent,
  DiagnosticVariableCellComponent,
  DynamicIconCellComponent,
  EmailTemplateCellComponent,
  EntityCellComponent,
  EventDurationCellComponent,
  FollowedNumberCellComponent,
  IconCellComponent,
  LastCommandStatusCellComponent,
  LinkedCellComponent,
  NumberCellComponent,
  OutgoingConnectorCellComponent,
  RichVariableValueCellComponent,
  SeverityCellComponent,
  TagCellComponent,
  TopicSelfSubscriptionCellComponent,
  TranslatedCellComponent,
  UserCellComponent,
  VariableDispatchCellComponent,
  AuditTrailAttributesCellComponent,
  DynamicDataCellComponent
];

const HEADER_CELLS_COMPONENTS = [
  HeaderShellComponent,
  BasicHeaderCellComponent,
  EmptyHeaderCellComponent,
  GroupHeaderCellComponent,
  IconAndTextHeaderCellComponent,
  IconHeaderCellComponent
];

const GRID_COMPONENTS = [
  ...FILTER_COMPONENTS,
  ...CELLS_COMPONENTS,
  ...HEADER_CELLS_COMPONENTS,
  PaginatorComponent,
  GridManagerComponent,
  GridPageComponent,
  GridEngineComponent,
  GridExportDialogComponent,
  GridExportComponent,
  GridManagerAdminPopupComponent,
  GridManagerUserPopupComponent,
  RowGroupInnerRendererCellComponent
];

@NgModule({
  imports: [
    CommonModule,
    TranslatePipe,
    EventSeverityDisplayModule,
    DragDropModule,
    StoreModule.forFeature(fromGrids.gridsFeatureKey, fromGrids.reducers),
    EffectsModule.forFeature([GridsEffects]),
    SubscriptionButtonModule,
    InfoDisplayPipe,
    NumberFormatPipe,
    DateFormatPipe,
    ValueUnitFormatPipe,
    CronDisplayPipe,
    UserNamePipe,
    ...GRID_COMPONENTS
  ],
  exports: [...GRID_COMPONENTS],
  providers: [CronDisplayPipe, DateFormatPipe, InfoDisplayPipe, NumberFormatPipe, UserNamePipe, ValueUnitFormatPipe, GridEngineSettingsService]
})
export class GridEngineModule {
  static withSettings(
    settings: Partial<{
      assetVariableConfigureReadPopupService: Type<AbstractGridAssetVariableConfigureReadPopupService>;
    }>
  ): ModuleWithProviders<GridEngineModule> {
    return {
      ngModule: GridEngineModule,
      providers: [
        {
          provide: AbstractGridAssetVariableConfigureReadPopupService,
          useClass: settings.assetVariableConfigureReadPopupService
        }
      ]
    };
  }

  static withTestingSettings(): ModuleWithProviders<GridEngineModule> {
    return {
      ngModule: GridEngineModule,
      providers: [
        {
          provide: AbstractGridAssetVariableConfigureReadPopupService,
          useValue: { updateAssetVariableThresholds: () => noop() }
        }
      ]
    };
  }
}
